import React, { useEffect, useMemo, useState } from 'react';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import {
  fetchHotelProposalReasons,
  type HotelProposalReasonsResult,
  type ProposalBundle
} from '../../api/proposalBundles';

type Status = 'idle' | 'loading' | 'completed' | 'unavailable' | 'error';

interface Props {
  tripId: number;
  bundle: ProposalBundle;
}

const HotelRecommendationReasonsSection = ({ tripId, bundle }: Props) => {
  const { theme } = useAITTheme();
  const styles = useMemo(() => getStyles(theme), [theme]);
  const [status, setStatus] = useState<Status>('idle');
  const [result, setResult] = useState<HotelProposalReasonsResult | null>(null);

  useEffect(() => {
    if (bundle.items.length === 0) return undefined;

    let cancelled = false;
    setStatus('loading');

    fetchHotelProposalReasons(String(tripId), bundle.id)
      .then(data => {
        if (cancelled) return;
        setResult(data);
        setStatus(data.status === 'completed' ? 'completed' : 'unavailable');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });

    return () => {
      cancelled = true;
    };
  }, [tripId, bundle.id, bundle.items.length]);

  const orderedReasons = useMemo(() => {
    if (!result) return [];
    const reasonByItemId = new Map(result.itemReasons.map(entry => [entry.itemId, entry.reason]));
    return bundle.items
      .map((item, index) => {
        const reason = reasonByItemId.get(item.id);
        if (!reason) return null;
        return { itemId: item.id, title: item.title, reason, index };
      })
      .filter(
        (entry): entry is { itemId: number; title: string | null; reason: string; index: number } => entry !== null
      );
  }, [result, bundle.items]);

  if (bundle.items.length === 0) return null;
  if (status === 'idle' || status === 'loading') {
    return <div style={styles.loading}>提案理由を読み込み中…</div>;
  }
  if (status !== 'completed' || !result) return null;
  if (orderedReasons.length === 0) return null;

  return (
    <div style={styles.container}>
      <div style={styles.header}>AIによるホテル提案の理由</div>
      {result.summaryReason && <div style={styles.summary}>{result.summaryReason}</div>}
      <ul style={styles.list}>
        {orderedReasons.map(entry => {
          const circleNumber = ['①', '②', '③', '④', '⑤'][entry.index] || `${entry.index + 1}`;
          return (
            <li key={entry.itemId} style={styles.listItem}>
              <span style={styles.circleNumber}>{circleNumber}</span>
              <div style={styles.listBody}>
                {entry.title && <div style={styles.itemTitle}>{entry.title}</div>}
                <div style={styles.itemReason}>{entry.reason}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

const getStyles = (theme: AITTheme) => ({
  loading: {
    marginTop: '12px',
    fontSize: '12px',
    color: theme.grayTextColor
  },
  container: {
    marginTop: '16px',
    padding: '12px 14px',
    backgroundColor: '#f8fbfd',
    borderRadius: '6px',
    border: `1px solid ${theme.grayBorderColor}`
  },
  header: {
    fontSize: '13px',
    fontWeight: 600 as const,
    color: theme.linkColor,
    marginBottom: '8px'
  },
  summary: {
    fontSize: '12px',
    lineHeight: 1.6,
    color: theme.textColor,
    marginBottom: '10px'
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '8px'
  },
  listItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px'
  },
  circleNumber: {
    width: '22px',
    height: '22px',
    minWidth: '22px',
    backgroundColor: theme.linkColor,
    color: '#ffffff',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 700 as const,
    fontSize: '11px'
  },
  listBody: {
    flex: 1
  },
  itemTitle: {
    fontSize: '12px',
    fontWeight: 600 as const,
    color: theme.textColor,
    marginBottom: '2px'
  },
  itemReason: {
    fontSize: '12px',
    lineHeight: 1.6,
    color: theme.textColor
  }
});

export default HotelRecommendationReasonsSection;
